import React, { useEffect, useMemo, useRef, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import { printKotById } from '../utils/kotPrint';
import { playChime, unlockAudio } from '../utils/chime';

const fmtKotTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};
const elapsedMin = (ts) => {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (diff < 1) return '<1m';
  if (diff < 60) return `${diff}m`;
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
};
// ER-MOM-05 Phase 2: prep-SLA status for a KOT (raised → ready). While cooking,
// measures against "now"; once READY/SERVED, against the actual prep time.
const slaInfo = (k) => {
  const sla = Number(k.sla_minutes);
  const raised = k.raised_at ? new Date(k.raised_at).getTime() : null;
  if (!sla || sla <= 0 || !raised) return null;
  const done = k.status === 'READY' || k.status === 'SERVED';
  const endMs = done && k.ready_at ? new Date(k.ready_at).getTime() : Date.now();
  const mins = Math.max(0, Math.floor((endMs - raised) / 60000));
  let state = 'ok';
  if (k.sla_breached || mins >= sla) state = 'breach';
  else if (mins >= sla * 0.8) state = 'warn';
  return { sla, mins, state, live: !done, remaining: Math.max(0, sla - mins) };
};
const SLA_COLOR = { breach: '#dc2626', warn: '#f59e0b', ok: '#22c55e' };

function KitchenPunchBoard({ token, selectedProperty, selectedOutletId = '' }) {
  const { isMobile } = useIsMobile();
  const [scope, setScope] = useState({
    chain_id: selectedProperty?.chain_id || '',
    property_id: selectedProperty?.id || '',
    outlet_id: selectedOutletId || ''
  });
  const [stations, setStations] = useState([]);
  const [selectedStationId, setSelectedStationId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  // ER-MOM-05: instant new-order alert (chime + visual) via staff WebSocket.
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [newKotIds, setNewKotIds] = useState(() => new Set());
  const soundEnabledRef = useRef(soundEnabled);
  const selectedStationIdRef = useRef(selectedStationId);
  useEffect(() => { soundEnabledRef.current = soundEnabled; });
  useEffect(() => { selectedStationIdRef.current = selectedStationId; });
  // ER-MOM-05 Phase 3: SLA-breach report panel.
  const today = new Date().toISOString().slice(0, 10);
  const [showSlaReport, setShowSlaReport] = useState(false);
  const [slaReport, setSlaReport] = useState(null);
  const [srFrom, setSrFrom] = useState(today);
  const [srTo, setSrTo] = useState(today);
  const [srLoading, setSrLoading] = useState(false);

  // P-E: staff push
  const [activeSessions, setActiveSessions] = useState([]);
  const [pushableOffers, setPushableOffers] = useState([]);
  const [pushModal, setPushModal] = useState({ open: false, session: null });
  const [pushing, setPushing] = useState(false);
  // Outlet type drives which offer engine's endpoints we hit for staff push.
  // BAR  → /api/v1/bar/sessions/* + /api/v1/bar/masters/offers
  // else → /api/v1/kot/sessions/* + /api/v1/kot/masters/offers
  // See docs/SRS_BAR_OFFER_ENGINE.md §8.3.
  const [outletType, setOutletType] = useState('');
  const isBar = String(outletType || '').toUpperCase() === 'BAR';
  const sessionsBase = isBar ? '/api/v1/bar/sessions' : '/api/v1/kot/sessions';
  const offersListUrl = isBar ? '/api/v1/bar/masters/offers' : '/api/v1/kot/masters/offers';

  const scopeHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': scope.chain_id,
    'x-property-id': scope.property_id,
    'x-outlet-id': scope.outlet_id
  });

  const loadStations = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setStations([]);
    try {
      const res = await fetch('/api/v1/kot/masters/stations', { headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load stations');
      const list = Array.isArray(data) ? data : [];
      setStations(list);
      if (selectedStationId && !list.some((s) => String(s.id) === String(selectedStationId))) {
        setSelectedStationId('');
      }
    } catch {
      setError('Network error while loading stations');
    }
  };

  const loadKots = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setRows([]);
    try {
      const query = new URLSearchParams();
      if (selectedStationId) query.set('station_id', selectedStationId);
      if (statusFilter) query.set('status', statusFilter);
      const suffix = query.toString() ? `?${query.toString()}` : '';
      const res = await fetch(`/api/v1/kot/kots${suffix}`, { headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load KOT queue');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading KOT queue');
    }
  };

  // Resolve outlet_type for the current scope so the staff-push endpoints
  // dispatch to the right engine (bar vs kot). Fetched once per outlet change.
  const loadOutletType = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setOutletType('');
    try {
      const res = await fetch(
        `/api/v1/core/outlets?property_id=${encodeURIComponent(scope.property_id)}`,
        { headers: scopeHeaders() }
      );
      if (!res.ok) return setOutletType('');
      const data = await res.json().catch(() => []);
      const list = Array.isArray(data) ? data : (data?.outlets || []);
      const me = list.find((o) => String(o.id) === String(scope.outlet_id));
      setOutletType(String(me?.outlet_type || '').toUpperCase());
    } catch { /* silent */ }
  };

  const loadActiveSessions = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setActiveSessions([]);
    try {
      const res = await fetch(`${sessionsBase}/active`, { headers: scopeHeaders() });
      if (!res.ok) return;  // role may not have access — silently hide
      const data = await res.json().catch(() => []);
      setActiveSessions(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  const loadPushableOffers = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setPushableOffers([]);
    try {
      const res = await fetch(offersListUrl, { headers: scopeHeaders() });
      if (!res.ok) return;
      const data = await res.json().catch(() => []);
      const list = Array.isArray(data) ? data : [];
      setPushableOffers(list.filter(o => o.is_active && o.staff_pushable));
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (selectedProperty?.chain_id && selectedProperty?.id) {
      setScope((prev) => ({ ...prev, chain_id: selectedProperty.chain_id, property_id: selectedProperty.id, outlet_id: String(selectedOutletId || '') }));
    } else {
      setScope({ chain_id: '', property_id: '', outlet_id: '' });
      setStations([]);
      setRows([]);
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (!selectedOutletId) return;
    setScope((prev) => ({ ...prev, outlet_id: String(selectedOutletId) }));
  }, [selectedOutletId]);

  useEffect(() => {
    if (!scope.outlet_id) return;
    loadStations();
    loadOutletType();
  }, [scope.outlet_id]);

  // Sessions + pushable offers fetched AFTER outlet type is known so the
  // right endpoint is hit from the first render (no KOT→bar flicker).
  useEffect(() => {
    if (!scope.outlet_id) return;
    loadActiveSessions();
    loadPushableOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.outlet_id, outletType]);
  useEffect(() => { loadKots(); }, [scope.outlet_id, selectedStationId, statusFilter]);

  // Keep refs pointing at the latest versions so the interval never uses a stale closure.
  const loadKotsRef = useRef(loadKots);
  const loadActiveSessionsRef = useRef(loadActiveSessions);
  useEffect(() => { loadKotsRef.current = loadKots; });
  useEffect(() => { loadActiveSessionsRef.current = loadActiveSessions; });

  useEffect(() => {
    if (!autoRefresh || !scope.outlet_id) return undefined;
    const timer = setInterval(() => {
      loadKotsRef.current();
      loadActiveSessionsRef.current();
    }, 7000);
    return () => clearInterval(timer);
  }, [autoRefresh, scope.outlet_id]);

  // ER-MOM-05: instant "new order" push via the staff WebSocket. The 7s poll
  // above stays as a fallback if the socket drops. Station/status are read via
  // refs so switching them doesn't reconnect the socket.
  useEffect(() => {
    if (!scope.property_id || !scope.outlet_id || !token) return undefined;
    let ws = null;
    let reconnectTimer = null;
    let closed = false;
    let attempt = 0;

    const scheduleReconnect = () => {
      if (closed) return;
      attempt += 1;
      const delay = Math.min(1000 * 2 ** attempt, 30000);
      reconnectTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (closed) return;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${proto}://${window.location.host}/ws/kitchen`
        + `?token=${encodeURIComponent(token)}`
        + `&property_id=${encodeURIComponent(scope.property_id)}`
        + `&outlet_id=${encodeURIComponent(scope.outlet_id)}`;
      try { ws = new WebSocket(url); } catch { scheduleReconnect(); return; }

      ws.onopen = () => { attempt = 0; setWsConnected(true); };
      ws.onmessage = (evt) => {
        let msg;
        try { msg = JSON.parse(evt.data); } catch { return; }
        if (msg?.type !== 'KOT_RAISED') return;
        // If watching a single station, only alert for that station.
        const stn = selectedStationIdRef.current;
        if (stn && String(msg.station_id) !== String(stn)) { loadKotsRef.current(); return; }
        if (soundEnabledRef.current) playChime(2);
        if (msg.kot_id != null) {
          setNewKotIds((prev) => { const n = new Set(prev); n.add(msg.kot_id); return n; });
          setTimeout(() => setNewKotIds((prev) => { const n = new Set(prev); n.delete(msg.kot_id); return n; }), 30000);
        }
        const tag = msg.order_type === 'ROOM_SERVICE'
          ? ` (Room Service${msg.room_number ? ' ' + msg.room_number : ''})`
          : msg.order_type === 'SELF_ORDER' ? ' (Self-Order)' : '';
        setMessage(`🔔 New order — KOT #${msg.kot_no ?? ''}${tag}`);
        loadKotsRef.current();
      };
      ws.onclose = () => { setWsConnected(false); scheduleReconnect(); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
    };
  }, [scope.property_id, scope.outlet_id, token]);

  useEffect(() => {
    const onKotRaised = () => {
      loadKots();
    };
    window.addEventListener('kot-raised', onKotRaised);
    return () => window.removeEventListener('kot-raised', onKotRaised);
  }, [scope.outlet_id, selectedStationId, statusFilter]);

  const loadSlaReport = async () => {
    if (!scope.chain_id || !scope.property_id) return;
    setSrLoading(true);
    try {
      const res = await fetch(`/api/v1/kot/sla-report?from=${srFrom}&to=${srTo}`, { headers: scopeHeaders() });
      const data = await res.json().catch(() => null);
      if (res.ok) setSlaReport(data);
      else setError(data?.error || 'Failed to load SLA report');
    } catch {
      setError('Network error while loading SLA report');
    } finally { setSrLoading(false); }
  };

  const performAction = async (id, action) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/kot/kots/${id}/${action}`, { method: 'POST', headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || `Failed to ${action}`);
      setMessage(`KOT ${action} success`);
      loadKots();
    } catch {
      setError(`Network error while ${action}`);
    }
  };

  const pushOfferToSession = async (offerId) => {
    if (!pushModal.session?.id) return;
    setPushing(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${sessionsBase}/${pushModal.session.id}/push-offer`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ offer_id: offerId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to push offer'); return; }
      setMessage(`✓ Offer sent to ${pushModal.session.mobile_masked || 'customer'}`);
      setPushModal({ open: false, session: null });
    } catch {
      setError('Network error while pushing offer');
    } finally {
      setPushing(false);
    }
  };

  const stationNameById = (id) => stations.find((s) => String(s.id) === String(id))?.name || id || '-';
  const canStart = (row) => row.status === 'RAISED';
  const canReady = (row) => row.status === 'PREPARING';
  const canServe = (row) => row.status === 'READY';

  const grouped = useMemo(() => {
    const base = { RAISED: [], PREPARING: [], READY: [], SERVED: [] };
    rows.forEach((r) => {
      const key = String(r.status || '').toUpperCase();
      if (base[key]) base[key].push(r);
    });
    return base;
  }, [rows]);

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Kitchen Punch Board</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div style={{ color: '#2f3640', marginBottom: '0.7rem' }}>{message}</div>}

      <div className="filter-bar">
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
          <select value={selectedStationId} onChange={(e) => setSelectedStationId(e.target.value)} style={inputStyle}>
            <option value="">All Stations</option>
            {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={inputStyle}>
            <option value="">All Status</option>
            <option value="RAISED">RAISED</option>
            <option value="PREPARING">PREPARING</option>
            <option value="READY">READY</option>
            <option value="SERVED">SERVED</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto Refresh (7s)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} title="Play a chime when a new order arrives">
            <input type="checkbox" checked={soundEnabled} onChange={async (e) => {
              if (e.target.checked) { await unlockAudio(); playChime(1); }
              setSoundEnabled(e.target.checked);
            }} />
            🔔 Sound alert
            <span title={wsConnected ? 'Live — instant new-order alerts' : 'Reconnecting… using 7s refresh'}
              style={{ width: 9, height: 9, borderRadius: '50%', background: wsConnected ? '#22c55e' : '#cbd5e1', display: 'inline-block' }} />
          </label>
        </div>
        <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={loadKots} className="btn-muted" style={{ padding: '0.5rem 0.8rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Refresh</button>
          <button type="button" onClick={() => { setShowSlaReport((v) => !v); if (!slaReport) loadSlaReport(); }}
            style={{ padding: '0.5rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '6px', background: showSlaReport ? 'var(--theme-primary)' : '#fff', color: showSlaReport ? '#fff' : '#334155', cursor: 'pointer' }}>
            SLA Report
          </button>
        </div>
      </div>

      {/* ─── ER-MOM-05 Phase 3: SLA breach report ─── */}
      {showSlaReport && (
        <div className="filter-bar" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
            <strong style={{ color: 'var(--theme-primary-dark)' }}>Food Prep SLA Breaches</strong>
            <span style={{ flex: 1 }} />
            <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>From:</label>
            <input type="date" value={srFrom} max={srTo} onChange={(e) => setSrFrom(e.target.value)} style={{ padding: '0.3rem 0.5rem', borderRadius: 5, border: '1px solid #cbd5e1', fontSize: '0.82rem' }} />
            <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>To:</label>
            <input type="date" value={srTo} min={srFrom} onChange={(e) => setSrTo(e.target.value)} style={{ padding: '0.3rem 0.5rem', borderRadius: 5, border: '1px solid #cbd5e1', fontSize: '0.82rem' }} />
            <button type="button" onClick={loadSlaReport} disabled={srLoading} style={{ padding: '0.35rem 0.9rem', borderRadius: 5, border: 'none', background: 'var(--theme-primary)', color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
              {srLoading ? 'Loading…' : 'Load'}
            </button>
          </div>

          {slaReport?.summary && (
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
              {[
                { label: 'Breaches', val: slaReport.summary.total_breaches, bg: '#fef2f2', color: '#b91c1c' },
                { label: 'Stations', val: slaReport.summary.stations_affected, bg: '#fff7ed', color: '#c2410c' },
                { label: 'Avg Overrun', val: slaReport.summary.avg_overrun_mins != null ? `+${slaReport.summary.avg_overrun_mins}m` : '-', bg: '#fffbeb', color: '#92400e' },
                { label: 'Worst', val: slaReport.summary.max_overrun_mins != null ? `+${slaReport.summary.max_overrun_mins}m` : '-', bg: '#faf5ff', color: '#6b21a8' },
                { label: 'Notified', val: slaReport.summary.notified_breaches, bg: '#eff6ff', color: '#1e40af' },
              ].map((k) => (
                <div key={k.label} style={{ background: k.bg, padding: '0.4rem 0.9rem', borderRadius: 6, minWidth: 84 }}>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{k.label}</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: k.color }}>{k.val}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', background: '#fff' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Breached At</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>KOT</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Station</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '0.45rem', textAlign: 'right' }}>SLA</th>
                  <th style={{ padding: '0.45rem', textAlign: 'right' }}>Elapsed</th>
                  <th style={{ padding: '0.45rem', textAlign: 'right' }}>Overrun</th>
                  <th style={{ padding: '0.45rem', textAlign: 'center' }}>Notified</th>
                </tr>
              </thead>
              <tbody>
                {(slaReport?.rows || []).map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                    <td style={{ padding: '0.4rem', whiteSpace: 'nowrap' }}>{r.breached_at ? new Date(r.breached_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</td>
                    <td style={{ padding: '0.4rem', fontWeight: 600 }}>#{r.kot_no}</td>
                    <td style={{ padding: '0.4rem' }}>{r.station_name || '-'}</td>
                    <td style={{ padding: '0.4rem' }}>{(r.order_type || '').replace(/_/g, ' ') || '-'}</td>
                    <td style={{ padding: '0.4rem', textAlign: 'right' }}>{r.sla_minutes}m</td>
                    <td style={{ padding: '0.4rem', textAlign: 'right' }}>{r.elapsed_minutes}m</td>
                    <td style={{ padding: '0.4rem', textAlign: 'right', color: '#b91c1c', fontWeight: 600 }}>+{r.overrun_minutes}m</td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }}>{r.notified > 0 ? <span title={`${r.notified} of ${r.recipients} notified`} style={{ color: '#166534', fontWeight: 700 }}>✓ {r.notified}</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  </tr>
                ))}
                {(!slaReport?.rows || slaReport.rows.length === 0) && !srLoading && (
                  <tr><td colSpan={8} style={{ padding: '1rem', textAlign: 'center', color: '#7f8c8d' }}>No SLA breaches in this range. 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Active self-order tables (P-E staff push surface) ─── */}
      {activeSessions.length > 0 && (
        <div style={{
          background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10,
          padding: '0.7rem 0.8rem', marginBottom: '0.9rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <strong style={{ color: '#3730a3' }}>
              📱 Active Self-Order Tables ({activeSessions.length})
              {isBar && (
                <span style={{
                  marginLeft: 8, padding: '1px 8px', borderRadius: 999,
                  background: '#ddd6fe', color: '#5b21b6',
                  fontSize: '0.68rem', fontWeight: 700, verticalAlign: 'middle',
                }}>BAR</span>
              )}
            </strong>
            <span style={{ fontSize: '0.72rem', color: '#6366f1' }}>Push offers to customers' phones</span>
          </div>
          <div style={{
            display: 'grid', gap: '0.5rem',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
          }}>
            {activeSessions.map(s => (
              <div key={s.id} style={{
                background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8,
                padding: '0.55rem 0.7rem', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', gap: '0.5rem',
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e1b4b' }}>
                    Table {s.table_no || s.table_name || '—'}
                  </div>
                  <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
                    {s.mobile_masked} · ₹{Number(s.running_total || 0).toFixed(0)} · {Number(s.ordered_count || 0)} orders
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPushModal({ open: true, session: s })}
                  disabled={!pushableOffers.length}
                  title={!pushableOffers.length ? 'No staff-pushable offers configured for this outlet' : 'Push an offer to this table'}
                  style={{
                    background: pushableOffers.length ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : '#cbd5e1',
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '0.4rem 0.7rem', fontSize: '0.78rem', fontWeight: 700,
                    cursor: pushableOffers.length ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap',
                  }}>🎁 Push</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.8rem' }}>
        {['RAISED', 'PREPARING', 'READY'].map((col) => (
          <div key={col} style={{ background: '#f5f6fa', borderRadius: '8px', padding: '0.8rem' }}>
            <h3 style={{ marginTop: 0 }}>{col} ({grouped[col].length})</h3>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {grouped[col].length === 0 ? (
                <div style={{ color: '#7f8c8d' }}>No tickets</div>
              ) : grouped[col].map((k) => {
                const isNew = newKotIds.has(k.id);
                const sla = slaInfo(k);
                const slaColor = sla ? SLA_COLOR[sla.state] : '#dcdde1';
                return (
                <div key={k.id} style={{
                  border: `${sla && sla.state !== 'ok' ? '2px' : '1px'} solid ${slaColor}`, borderRadius: '8px',
                  background: sla?.state === 'breach' ? '#fef2f2' : (isNew ? '#fffbeb' : '#fff'), padding: '0.7rem',
                  boxShadow: isNew ? '0 0 0 3px #fde68a' : 'none', transition: 'box-shadow 0.3s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      KOT #{k.kot_no}
                      {isNew && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#f59e0b', color: '#fff', fontSize: '0.62rem', fontWeight: 800, verticalAlign: 'middle' }}>NEW</span>}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>{elapsedMin(k.raised_at)} ago</div>
                  </div>
                  <div style={{ fontSize: '0.88rem', color: '#555' }}>Order: {k.order_id}</div>
                  <div style={{ fontSize: '0.88rem', color: '#555' }}>Station: {stationNameById(k.station_id)}</div>
                  <div style={{ fontSize: '0.78rem', color: '#777', marginTop: '0.25rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {k.raised_at && <span>Raised: {fmtKotTime(k.raised_at)}</span>}
                    {k.status === 'PREPARING' && (k.prep_started_at || k.raised_at) && <span style={{ color: '#d97706' }}>Started: {fmtKotTime(k.prep_started_at || k.updated_at || k.raised_at)}</span>}
                    {k.ready_at && <span style={{ color: '#059669' }}>Ready: {fmtKotTime(k.ready_at)}</span>}
                    {k.served_at && <span style={{ color: '#6b7280' }}>Served: {fmtKotTime(k.served_at)}</span>}
                    {sla && (
                      <span title={`Prep SLA ${sla.sla} min`} style={{ fontWeight: 700, color: '#fff', background: slaColor, borderRadius: 4, padding: '1px 6px' }}>
                        {sla.state === 'breach' ? '⚠ ' : ''}SLA {sla.mins}/{sla.sla}m{sla.live && sla.state !== 'breach' ? ` · ${sla.remaining}m left` : ''}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: '0.55rem', display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                    <button onClick={() => performAction(k.id, 'start')} disabled={!canStart(k)} style={{ padding: '0.5rem 0.8rem', border: 'none', borderRadius: '6px', cursor: canStart(k) ? 'pointer' : 'not-allowed' }} className={canStart(k) ? 'btn-primary' : 'btn-muted'}>Start</button>
                    <button onClick={() => performAction(k.id, 'ready')} disabled={!canReady(k)} style={{ padding: '0.5rem 0.8rem', border: 'none', borderRadius: '6px', cursor: canReady(k) ? 'pointer' : 'not-allowed' }} className={canReady(k) ? 'btn-warning' : 'btn-muted'}>Ready</button>
                    <button onClick={() => performAction(k.id, 'served')} disabled={!canServe(k)} style={{ padding: '0.5rem 0.8rem', border: 'none', borderRadius: '6px', cursor: canServe(k) ? 'pointer' : 'not-allowed' }} className={canServe(k) ? 'btn-success' : 'btn-muted'}>Served</button>
                    <button onClick={async () => { try { await printKotById(k.id, scopeHeaders(), selectedProperty?.name || ''); } catch (err) { setError(err?.message || 'Failed to print KOT'); } }}
                      style={{ padding: '0.5rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#fff', color: '#334155', cursor: 'pointer' }}>Print</button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ─── Push Offer modal ─── */}
      {pushModal.open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(15,23,42,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            width: '100%', maxWidth: 520, background: '#fff', borderRadius: 16,
            padding: '1.2rem 1.1rem', maxHeight: '85vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
              <h3 style={{ margin: 0, color: '#1e1b4b' }}>
                Push Offer to Table {pushModal.session?.table_no || pushModal.session?.table_name || '—'}
              </h3>
              <button
                onClick={() => setPushModal({ open: false, session: null })}
                style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#64748b' }}>
              Customer phone: {pushModal.session?.mobile_masked} · Sending shows a toast on their screen at the next poll (~20 s).
            </p>

            {pushableOffers.length === 0 ? (
              <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: 8, color: '#92400e', fontSize: '0.85rem' }}>
                No staff-pushable offers configured for this outlet. Add one in <strong>Masters → Offers</strong>.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.55rem' }}>
                {pushableOffers.map(o => (
                  <button
                    key={o.id}
                    disabled={pushing}
                    onClick={() => pushOfferToSession(o.id)}
                    style={{
                      textAlign: 'left', padding: '0.75rem 0.9rem',
                      border: '1px solid #e2e8f0', borderRadius: 10,
                      background: '#f8fafc', cursor: pushing ? 'not-allowed' : 'pointer',
                      opacity: pushing ? 0.6 : 1,
                    }}>
                    <div style={{ fontWeight: 700, color: '#1e1b4b' }}>{o.title}</div>
                    {o.description && <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 3 }}>{o.description}</div>}
                    <div style={{ fontSize: '0.72rem', color: '#6366f1', marginTop: 5 }}>
                      {o.rule_type} · {o.reward_type} {o.reward_value !== null && o.reward_value !== undefined ? `(${o.reward_value})` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default KitchenPunchBoard;
